import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

// Translations
const translations = {
  en: {
    // Navigation
    home: 'Home',
    dashboard: 'Dashboard',
    lessons: 'Lessons',
    profile: 'Profile',
    logout: 'Logout',
    login: 'Login',
    register: 'Register',
    
    // Landing page
    heroTitle: 'Learn German with EGNS',
    heroSubtitle: 'Master German from A1 to B2 with AI-powered teachers Herr Amr and Frau Rasha',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    features: 'Features',
    aiTeachers: 'AI Teachers',
    aiTeachersDesc: 'Learn with two unique AI personalities - Herr Amr for grammar and Frau Rasha for conversation',
    speechRecognition: 'Speech Recognition',
    speechRecognitionDesc: 'Practice pronunciation with real-time feedback and correction',
    gamification: 'Gamification',
    gamificationDesc: 'Earn points, badges, and maintain streaks to stay motivated',
    medicalGerman: 'Medical German',
    medicalGermanDesc: 'Specialized content for nursing professionals',
    
    // Auth
    email: 'Email',
    password: 'Password',
    name: 'Name',
    confirmPassword: 'Confirm Password',
    forgotPassword: 'Forgot Password?',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    orContinueWith: 'Or continue with',
    google: 'Google',
    
    // Dashboard
    welcomeBack: 'Welcome back',
    yourProgress: 'Your Progress',
    currentLevel: 'Current Level',
    totalPoints: 'Total Points',
    streakDays: 'Day Streak',
    lessonsCompleted: 'Lessons Completed',
    continueLearning: 'Continue Learning',
    recentActivity: 'Recent Activity',
    achievements: 'Achievements',
    
    // Lessons
    allLevels: 'All Levels',
    beginner: 'Beginner',
    elementary: 'Elementary',
    intermediate: 'Intermediate',
    upperIntermediate: 'Upper Intermediate',
    startLesson: 'Start Lesson',
    continueLesson: 'Continue',
    completed: 'Completed',
    locked: 'Locked',
    minutes: 'min',
    points: 'points',
    
    // Lesson view
    speakNow: 'Speak Now',
    stopRecording: 'Stop Recording',
    sendMessage: 'Send Message',
    typeMessage: 'Type your message...',
    listening: 'Listening...',
    processing: 'Processing...',
    grammarTip: 'Grammar Tip',
    vocabularyList: 'Vocabulary',
    objectives: 'Objectives',
    
    // Profile
    settings: 'Settings',
    language: 'Language',
    notifications: 'Notifications',
    saveChanges: 'Save Changes',
    
    // Teachers
    herrAmr: 'Herr Amr',
    herrAmrRole: 'Grammar Expert',
    frauRasha: 'Frau Rasha',
    frauRashaRole: 'Conversation Coach',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    save: 'Save',
    back: 'Back',
    next: 'Next',
    finish: 'Finish',
  },
  de: {
    // Navigation
    home: 'Startseite',
    dashboard: 'Dashboard',
    lessons: 'Lektionen',
    profile: 'Profil',
    logout: 'Abmelden',
    login: 'Anmelden',
    register: 'Registrieren',
    
    // Landing page
    heroTitle: 'Deutsch lernen mit EGNS',
    heroSubtitle: 'Meistern Sie Deutsch von A1 bis B2 mit KI-Lehrern Herr Amr und Frau Rasha',
    getStarted: 'Jetzt starten',
    learnMore: 'Mehr erfahren',
    features: 'Funktionen',
    aiTeachers: 'KI-Lehrer',
    aiTeachersDesc: 'Lernen Sie mit zwei einzigartigen KI-Persönlichkeiten - Herr Amr für Grammatik und Frau Rasha für Konversation',
    speechRecognition: 'Spracherkennung',
    speechRecognitionDesc: 'Üben Sie die Aussprache mit Echtzeit-Feedback und Korrektur',
    gamification: 'Spielelemente',
    gamificationDesc: 'Sammeln Sie Punkte, Abzeichen und halten Sie Ihre Serie aufrecht',
    medicalGerman: 'Medizinisches Deutsch',
    medicalGermanDesc: 'Spezialisierte Inhalte für Pflegekräfte',
    
    // Auth
    email: 'E-Mail',
    password: 'Passwort',
    name: 'Name',
    confirmPassword: 'Passwort bestätigen',
    forgotPassword: 'Passwort vergessen?',
    noAccount: 'Kein Konto?',
    haveAccount: 'Haben Sie bereits ein Konto?',
    orContinueWith: 'Oder fortfahren mit',
    google: 'Google',
    
    // Dashboard
    welcomeBack: 'Willkommen zurück',
    yourProgress: 'Ihr Fortschritt',
    currentLevel: 'Aktuelles Niveau',
    totalPoints: 'Gesamtpunkte',
    streakDays: 'Tage Serie',
    lessonsCompleted: 'Abgeschlossene Lektionen',
    continueLearning: 'Weiter lernen',
    recentActivity: 'Letzte Aktivität',
    achievements: 'Erfolge',
    
    // Lessons
    allLevels: 'Alle Niveaus',
    beginner: 'Anfänger',
    elementary: 'Grundlegend',
    intermediate: 'Mittelstufe',
    upperIntermediate: 'Obere Mittelstufe',
    startLesson: 'Lektion starten',
    continueLesson: 'Fortsetzen',
    completed: 'Abgeschlossen',
    locked: 'Gesperrt',
    minutes: 'Min',
    points: 'Punkte',
    
    // Lesson view
    speakNow: 'Jetzt sprechen',
    stopRecording: 'Aufnahme stoppen',
    sendMessage: 'Nachricht senden',
    typeMessage: 'Nachricht eingeben...',
    listening: 'Höre zu...',
    processing: 'Verarbeite...',
    grammarTip: 'Grammatik-Tipp',
    vocabularyList: 'Vokabeln',
    objectives: 'Ziele',
    
    // Profile
    settings: 'Einstellungen',
    language: 'Sprache',
    notifications: 'Benachrichtigungen',
    saveChanges: 'Änderungen speichern',
    
    // Teachers
    herrAmr: 'Herr Amr',
    herrAmrRole: 'Grammatik-Experte',
    frauRasha: 'Frau Rasha',
    frauRashaRole: 'Konversations-Coach',
    
    // Common
    loading: 'Lädt...',
    error: 'Fehler',
    success: 'Erfolg',
    cancel: 'Abbrechen',
    save: 'Speichern',
    back: 'Zurück',
    next: 'Weiter',
    finish: 'Beenden',
  },
  ar: {
    // Navigation
    home: 'الرئيسية',
    dashboard: 'لوحة التحكم',
    lessons: 'الدروس',
    profile: 'الملف الشخصي',
    logout: 'تسجيل الخروج',
    login: 'تسجيل الدخول',
    register: 'إنشاء حساب',
    
    // Landing page
    heroTitle: 'تعلم الألمانية مع EGNS',
    heroSubtitle: 'أتقن الألمانية من A1 إلى B2 مع المعلمين الذكاء الاصطناعي هير أمر وفراو رشا',
    getStarted: 'ابدأ الآن',
    learnMore: 'اعرف المزيد',
    features: 'المميزات',
    aiTeachers: 'معلمون بالذكاء الاصطناعي',
    aiTeachersDesc: 'تعلم مع شخصيتين فريدتين - هير أمر للقواعد وفراو رشا للمحادثة',
    speechRecognition: 'التعرف على الصوت',
    speechRecognitionDesc: 'تدرب على النطق مع تصحيح فوري',
    gamification: 'عناصر اللعب',
    gamificationDesc: 'اكسب نقاط وشارات وحافظ على سلسلتك',
    medicalGerman: 'الألمانية الطبية',
    medicalGermanDesc: 'محتوى متخصص للممرضين',
    
    // Auth
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    name: 'الاسم',
    confirmPassword: 'تأكيد كلمة المرور',
    forgotPassword: 'نسيت كلمة المرور؟',
    noAccount: 'ليس لديك حساب؟',
    haveAccount: 'لديك حساب بالفعل؟',
    orContinueWith: 'أو تابع مع',
    google: 'جوجل',
    
    // Dashboard
    welcomeBack: 'مرحبًا بعودتك',
    yourProgress: 'تقدمك',
    currentLevel: 'المستوى الحالي',
    totalPoints: 'إجمالي النقاط',
    streakDays: 'أيام متتالية',
    lessonsCompleted: 'الدروس المكتملة',
    continueLearning: 'تابع التعلم',
    recentActivity: 'النشاط الأخير',
    achievements: 'الإنجازات',
    
    // Lessons
    allLevels: 'جميع المستويات',
    beginner: 'مبتدئ',
    elementary: 'أساسي',
    intermediate: 'متوسط',
    upperIntermediate: 'فوق المتوسط',
    startLesson: 'ابدأ الدرس',
    continueLesson: 'تابع',
    completed: 'مكتمل',
    locked: 'مغلق',
    minutes: 'دقيقة',
    points: 'نقاط',
    
    // Lesson view
    speakNow: 'تحدث الآن',
    stopRecording: 'أوقف التسجيل',
    sendMessage: 'أرسل الرسالة',
    typeMessage: 'اكتب رسالتك...',
    listening: 'أستمع...',
    processing: 'جاري المعالجة...',
    grammarTip: 'نصيحة قواعد',
    vocabularyList: 'المفردات',
    objectives: 'الأهداف',
    
    // Profile
    settings: 'الإعدادات',
    language: 'اللغة',
    notifications: 'الإشعارات',
    saveChanges: 'حفظ التغييرات',
    
    // Teachers
    herrAmr: 'هير أمر',
    herrAmrRole: 'خبير القواعد',
    frauRasha: 'فراو رشا',
    frauRashaRole: 'مدربة المحادثة',
    
    // Common
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجاح',
    cancel: 'إلغاء',
    save: 'حفظ',
    back: 'رجوع',
    next: 'التالي',
    finish: 'إنهاء',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('egns_language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('egns_language', language);
    // Set document direction for RTL languages
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const changeLanguage = (lang) => {
    if (['en', 'de', 'ar'].includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
